import React from 'react';

/**
 * Shared rendering of "these days are on a clock".
 *
 * Every leave balance in the app already carries `expiringGrants` on its snapshot — HR grants
 * with an expiry date, plus, for Comp-Off, the earned days themselves (see
 * leaveBalanceService's expiringGrants block). The data was there on every surface; only the
 * Leave Dashboard and the balance drawer ever read it, so a balance shown anywhere else was a
 * number with an invisible deadline attached. Comp-Off is the case that bites, because its
 * window runs from the date worked rather than the leave year, so days genuinely lapse mid-year.
 *
 * Centralised because the alternative is each screen formatting a date its own way. Comp-Off
 * expiry was already being written three different ways before this existed — a raw ISO string
 * in one table, a relative countdown in a dashboard chip, and a full sentence on Apply
 * Comp-Off. Same fact, three formats, and only one of them stated an actual date.
 */

export type ExpiringGrant = { days: number; expiresOn: string };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `2026-09-15` → `15 Sep 2026`.
 *
 * Spelled out because a bare ISO string sitting beside a day count reads as one more number in
 * a row of numbers. Deliberately not toLocaleDateString: that renders 09/15 or 15/09 depending
 * on the viewer's locale, and a deadline nobody can read off with certainty is worse than a
 * slightly longer one.
 */
export const formatExpiryDate = (iso: string): string => {
  const [y, m, d] = (iso || '').split('-').map(Number);
  if (!y || !m || !d || m < 1 || m > 12) return iso || '—';
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/** Whole days from today until `iso`; negative once it has passed. */
export const daysUntilExpiry = (iso: string): number => {
  const target = new Date(`${iso}T00:00:00`).getTime();
  if (Number.isNaN(target)) return 0;
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((target - midnight) / 86400000);
};

/**
 * How much time is left, in words.
 *
 * Zero is "last day today", not "0 days left" — a day is usable *through* its expiry date
 * (CompOffService.isExpired compares `today > expiry`), so a count of nought would read as
 * already gone on the one day it matters most that the holder acts.
 */
export const daysLeftLabel = (left: number): string => {
  if (left < 0) return 'expired';
  if (left === 0) return 'last day today';
  return `${left} ${left === 1 ? 'day' : 'days'} left`;
};

/** Grants collapsed to one entry per date, soonest first. */
export const groupExpiringGrants = (grants?: ExpiringGrant[]): ExpiringGrant[] => {
  if (!grants || grants.length === 0) return [];
  const byDate = new Map<string, number>();
  grants.forEach(g => {
    if (!g?.expiresOn || !(g.days > 0)) return;
    byDate.set(g.expiresOn, (byDate.get(g.expiresOn) || 0) + g.days);
  });
  return Array.from(byDate.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([expiresOn, days]) => ({ expiresOn, days: Math.round(days * 10) / 10 }));
};

/**
 * A one-line summary for places with room for a clause but not a list — an inline sentence,
 * a table cell, a tooltip.
 *
 * Names the soonest date in full and only counts the rest, rather than listing every date, so
 * it stays one line however many grants there are. Returns '' when nothing is expiring, so
 * callers can drop it with a plain falsy check instead of rendering an empty separator.
 */
export const expirySummary = (grants?: ExpiringGrant[]): string => {
  const groups = groupExpiringGrants(grants);
  if (groups.length === 0) return '';
  const [first] = groups;
  const head = `${first.days} expiring ${formatExpiryDate(first.expiresOn)}`;
  return groups.length === 1 ? head : `${head} (+${groups.length - 1} later)`;
};

/** The full breakdown as a tooltip string, for a compact surface that has a title attribute. */
export const expiryTooltip = (grants?: ExpiringGrant[]): string | undefined => {
  const groups = groupExpiringGrants(grants);
  if (groups.length === 0) return undefined;
  return groups
    .map(g => `${g.days} day(s) expire on ${formatExpiryDate(g.expiresOn)} — ${daysLeftLabel(daysUntilExpiry(g.expiresOn))}`)
    .join('\n');
};

/**
 * Stacked expiry notes, for a card or drawer with vertical room.
 *
 * One line per date. Renders nothing at all when nothing is expiring — an "expires: none" line
 * would be noise on the great majority of balances, which have no expiry at all.
 */
export const ExpiryNotes: React.FC<{ grants?: ExpiringGrant[]; className?: string }> = ({
  grants,
  className = 'lb-drawer-stat-note',
}) => {
  const groups = groupExpiringGrants(grants);
  if (groups.length === 0) return null;
  return (
    <>
      {groups.map(g => (
        <span key={g.expiresOn} className={className}>
          {g.days} expire{g.days === 1 ? 's' : ''} {formatExpiryDate(g.expiresOn)} · {daysLeftLabel(daysUntilExpiry(g.expiresOn))}
        </span>
      ))}
    </>
  );
};
